import { Box, Button, HStack, Select, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text, Card, CardHeader, CardBody, CardFooter, Heading, VStack, Progress } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { MdFontDownload } from "react-icons/md"; // Added MdPlayArrow and MdPause
import { FaMosque, FaRegPauseCircle, FaRegPlayCircle } from "react-icons/fa";

import sajdaData from '../data/sajdaData';
import { daysData } from '../data/daysData';

const Sura = () => {
  const { suraId } = useParams();
  const location = useLocation();
  const [sura, setSura] = useState();
  const [fontSize, setFontSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Add error state
  const suraAudioRef = useRef(null); // Renamed for clarity
  const ayahAudioRef = useRef(null); // Added for individual ayah audio
  const [playingAyahKey, setPlayingAyahKey] = useState(null); // To track playing ayah
  const [currentDay, setCurrentDay] = useState(null); // Added currentDay state

  const fontChanger = {
    indopak : {
      api : 'https://api.quran.com/api/v4/quran/verses/indopak?chapter_number=',
      fontFamily: 'indopak',
      versetext: 'text_indopak'
    },
    uthmani : {
      api : 'https://api.quran.com/api/v4/quran/verses/uthmani?chapter_number=',
      fontFamily: 'uthmani',
      versetext: 'text_uthmani'
    },
  }
  const [fontData, setFontData] = useState(fontChanger.indopak);

  useEffect(() => {
    const fetchSura = async () => {
      setIsLoading(true);
      setError(null);
      setSura(null);
      try {
        const response = await fetch(
          `${fontData.api}${suraId}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch sura data');
        }
        const data = await response.json();
        setSura(data); 
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSura();
  }, [suraId, fontData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dayQueryParam = searchParams.get('day');
    if (dayQueryParam && daysData[dayQueryParam]) {
      setCurrentDay(daysData[dayQueryParam]);
    } else {
      setCurrentDay(null);
    }
  }, [location.search]);

  // Helper function to format ayahId for audio URL
  const ayahIdIntoSixDigit = (input) => {
    if (!input || typeof input !== 'string' || !input.includes(':')) {
      return '000000'; // Return a default or handle error appropriately
    }
    const [x, y] = input.split(':');
    const padNumber = (num) => String(num).padStart(3, '0');
    return padNumber(x) + padNumber(y);
  };

  const handlePlayAyah = (verseKey) => {
    const audioSrc = `https://verses.quran.com/Alafasy/mp3/${ayahIdIntoSixDigit(verseKey)}.mp3`;
    if (ayahAudioRef.current) {
      if (playingAyahKey === verseKey && !ayahAudioRef.current.paused) {
        ayahAudioRef.current.pause();
        setPlayingAyahKey(null);
      } else {
        ayahAudioRef.current.src = audioSrc;
        ayahAudioRef.current.play()
          .then(() => setPlayingAyahKey(verseKey))
          .catch(err => console.error("Error playing audio:", err));
      }
    }
  };

  useEffect(() => {
    // Add event listener for when audio ends
    const currentAudioRef = ayahAudioRef.current;
    const onEnded = () => setPlayingAyahKey(null);
    if (currentAudioRef) {
      currentAudioRef.addEventListener('ended', onEnded);
    }
    return () => {
      if (currentAudioRef) {
        currentAudioRef.removeEventListener('ended', onEnded);
      }
    };
  }, [playingAyahKey]);


  if (isLoading) {
    return <div>Loading...</div>; // Display loading message
  }

  if (error) {
    return <div>Error: {error.message}</div>; // Display error message
  }

  let progressValue = 0;
  let completedSuras = 0;
  let totalSurasInDay = 0;

  const searchParams = new URLSearchParams(location.search);
  const dayQueryParam = searchParams.get('day');

  if (dayQueryParam && currentDay) {
    totalSurasInDay = currentDay.sura.length;
    const currentSuraIndexInDay = currentDay.sura.findIndex(s => s.id === parseInt(suraId));
    if (currentSuraIndexInDay !== -1) {
      completedSuras = currentSuraIndexInDay + 1;
      progressValue = (completedSuras / totalSurasInDay) * 100;
    }
  }

  return (
    <Box px='15px'>
      <Box pt='10px' px="10px" align='center'>
        <audio ref={suraAudioRef} controls>
          <source
            src={`https://download.quranicaudio.com/qdc/mishari_al_afasy/murattal/${suraId}.mp3`}
            type="audio/mpeg"
          />
          Your browser does not support the audio element.
        </audio>
      </Box>
      {/* Hidden audio element for individual ayahs */}
      <audio ref={ayahAudioRef} style={{ display: 'none' }} />


      <HStack p='10px'>
        <Select 
          onChange={(e) => setFontData(fontChanger[e.target.value])}
          value={fontData.fontFamily}
        >
          <option value="indopak" >Indopak</option>
          <option value="uthmani" >Uthmani</option>
        </Select>
        <Slider defaultValue={fontSize}  min={15} max={50}
          onChange={(value) => setFontSize(value)}
        >
          <SliderTrack bg='red.100'>
            <SliderFilledTrack bg='tomato' />
          </SliderTrack>
          <SliderThumb boxSize={6}>
            <Box color='tomato' as={MdFontDownload} />
          </SliderThumb>
        </Slider>
      </HStack>
      
      
      {sura && sura.verses && sura.verses.map((verse) => (
        <Box key={verse.id} borderBottom="1px" py="5px">
          {/* The HStack will be removed, and the Text will no longer need flex="1" */}
            <Text fontFamily={fontData.fontFamily} fontSize={fontSize} dir="rtl">
              {verse[fontData.versetext]}
              <Text as="span" fontFamily='body' fontSize={fontSize-10}>
                {' '}({verse.verse_key})
              </Text>
              {sajdaData[verse.verse_key] && (
                <Text as="span" color="teal.500" fontWeight="bold" ml="10px">
                  <FaMosque style={{ display: 'inline-block', marginRight: '5px' }} />
                  {' '}Sajda{' '}
                </Text>
              )}
              {/* The Button is now moved inside the Text component */}
              <Button
                size="xs"
                onClick={() => handlePlayAyah(verse.verse_key)}
                variant="ghost"
                aria-label={playingAyahKey === verse.verse_key ? "Pause Ayah" : "Play Ayah"}
                ml="10px"
                verticalAlign="middle"
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                {playingAyahKey === verse.verse_key ? <FaRegPauseCircle size="15px" /> : <FaRegPlayCircle size="15px" />}
              </Button>
            </Text>
          {/* The closing HStack is removed */}
        </Box>
      ))}
      {!dayQueryParam && (
        <HStack justifyContent="space-between" p="10px" mt="20px">
          <Button
            as={Link}
            to={`/sura/${parseInt(suraId) - 1}`}
            isDisabled={parseInt(suraId) <= 1}
          >
            Previous Sura
          </Button>
          <Button
            as={Link}
            to={`/sura/${parseInt(suraId) + 1}`}
            isDisabled={parseInt(suraId) >= 114}
          >
            Next Sura
          </Button>
        </HStack>
      )}

      {dayQueryParam && currentDay && (
        <Card mt="20px" variant="outline">
          <CardHeader>
            <Heading size="md">{currentDay.title}</Heading>
            {totalSurasInDay > 0 && (
              <Box mt="2">
                <Text fontSize="sm" mb="1" textAlign="right">
                  {completedSuras}/{totalSurasInDay}
                </Text>
                <Progress value={progressValue} size="sm" colorScheme="teal" borderRadius="md"/>
              </Box>
            )}
          </CardHeader>
          <CardBody pt="2"> {/* Adjusted paddingTop for CardBody */}
            <HStack spacing="10px" overflowX="auto" py="2">
              {currentDay.sura.map((s, index) => (
                <Card
                  key={s.id}
                  variant="outline"
                  minW="150px" // Ensure cards have a minimum width
                  bg={index < completedSuras ? (s.id === parseInt(suraId) ? 'teal.100' : 'green.50') : 'gray.50'}
                  borderColor={s.id === parseInt(suraId) ? 'teal.400' : (index < completedSuras ? 'green.200' : 'gray.200')}
                  opacity={index < completedSuras ? 1 : 0.7}
                >
                  <CardBody py="2" px="3">
                    <Text fontSize="sm" fontWeight={s.id === parseInt(suraId) ? 'bold' : 'semibold'}>
                      {s.id}. {s.name}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      ({s.ayah} ayahs)
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      ~{s.time}
                    </Text>
                  </CardBody>
                  <CardFooter py="1" px="3" borderTopWidth="1px" justifyContent="center">
                    <Button
                      as={Link}
                      to={`/sura/${s.id}?day=${dayQueryParam}`}
                      variant="link"
                      colorScheme="teal"
                      size="xs"
                    >
                      Read
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </HStack>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default Sura;
